import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, IconButton, Paper, Popper, TextField, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import LogoSym from './SVGLogoSym';
import { default as U, default as Utils } from '../../felles/Utils';
import MenuEl from '../../models/MenuEl';
import MenuItemTarget from '../../models/MenuItemTarget';
import SearchSel from '../../models/SearchSel';
import * as actions from '../../store/actions/index';
import { AppState } from '../../store/rootReducer';
import Enums from '../../felles/Enums';

const useStyles = makeStyles()((/*theme: Theme*/) =>
({
  customTooltip: { display: 'float', right: -50, backgroundColor: '#fff', fontSize: '1em', color: '#2E2D2C', },
  customArrow: { color: ' #fff', },
  //
  searchCss: { paddingTop: -5, width: 150, height: 45, backgroundColor: '#fff', flex: 1, },
  // 
  searchBar: {
    display: 'flex', marginBottom: 10,
    '& label': { height: 25, textAlign: 'center', color: '#d30535', },
    '& label.Mui-focused': { top: 12, animationIterationCount: 1, transform: 'translate(0, -10px) scale(1)', fontSize: '.75em', color: '#d30535', },
    '& .MuiInputLabel-formControl': { top: 11, left: 6, position: 'absolute', transform: 'translate(0, 0) scale(1)', color: '#d30535', },
    '& .MuiInput-underline:after': { height: 45, width: 0 },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { border: "none", },
  },
  //
  iconButton: { padding: 6, marginLeft: 2, backgroundColor: '#d30535', color: '#fff' },
  buttonRoot: { width: '100%', display: 'inline-block', textAlign: 'left' },
  buttonCode: { fontWeight: 600, color: '#d30535' },
  buttonDesc: { color: '#2E2D2C', marginLeft: 5, fontSize: '.9em' },
  buttonHeading: { textAlign: 'left', color: '#2E2D2C', marginLeft: 5, fontSize: '.9em', verticalAlign: 'bottom' },
  path: { display: 'inline-block', color: '#2E2D2C', width: 500, fontSize: '.9em' },
  rootName: { color: '#243367' },
  parentName: {},
  headName: { color: '#d30535' },
  //
  inputRoot: {
    paddingLeft: 15,
    paddingBottom: 7,
    paddingTop: 7,
    cursor: 'pointer',
    "&.Mui-focused": { background: '#f6f6f6', },
  },
})
);

const PopperMy = function (props) {
  return <Popper {...props} style={{ width: 550 }} placement="bottom-start" />;
};

export default function LPNavigationSearch() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedSearchOption: SearchSel = useSelector((state) => (state as AppState).search.searchSelectionOptions);
  const inputValue: string = useSelector((state) => (state as AppState).search.searchInputValue);
  //const isFilled: boolean = useSelector((state) => (state as AppState).search.isSearchInputPopulated);
  const menuItems: MenuEl[] = useSelector((state) => (state as AppState).menuItem.menuItems);
  const [searchVal, setSearchVal] = useState('');
  const isSearchFieldFocused: boolean = useSelector((state) => (state as AppState).search.isSearchFieldFocused);
  const formRef = useRef<HTMLDivElement>(null);
  const isTransGetInProgress: boolean = useSelector((state) => (state as AppState).trans.isTransGetInProgress);

  //
  useEffect(() => {
    if (isSearchFieldFocused) {
      const _target = formRef.current?.querySelector('input');
      if (_target) {
        _target.focus();
      }
    }
  }, [isSearchFieldFocused, isTransGetInProgress]);
  //
  const handleHomeClick = () => {
    // Refreshes news and operations feed
    dispatch(actions.nyheterMeldingFetchStarted());
    dispatch(actions.driftMeldingFetchStarted());
    //
    dispatch(actions.menuItemSetTarget(new MenuItemTarget()));
    dispatch(actions.setSearchSelectionOptions(new SearchSel()));
    dispatch(actions.setSearchInputPopulatedState(false));
    dispatch(actions.menuItemSetExpanded([undefined]));
    dispatch(actions.menuItemSetSelected([undefined, undefined]));
    navigate(Utils.HOMEPAGE_CONTEXT + '/menu');
  };
  //
  function handleRightClick(e, data) {
    e.preventDefault(); // Prevent default browser context menu for custom menu items
    const clickX = e.clientX;
    const clickY = e.clientY;
    dispatch(actions.setContextMenuVisible(true));
    dispatch(actions.setContexMenuX(clickX));
    dispatch(actions.setContexMenuY(clickY));
    dispatch(actions.setTransidRedirect(data.code));/**/
    dispatch(actions.setTilpasset(data.tilpasset));/**/
  }
  //
  const handleSearchClick = (e: any, selectedCode?: string) => {
    // Prevent form submit
    e?.preventDefault();
    e?.stopPropagation();

    dispatch(actions.setSandwichButtonStateClicked(false));

    // Reset predefined redux transaction store
    dispatch(actions.resetPredefinedReduxTransactionStore());

    // Use selectedCode if provided, otherwise fall back to searchVal
    const codeToSearch = selectedCode || searchVal;

    // If search value is less than 2 characters, do nothing
    if (codeToSearch.length < 2) {
      return;
    }
    // Find the matched element
    const options = menuOptions();
    const matchedElement = options.find(element => element.code.includes(codeToSearch));
    dispatch(actions.menuItemSetExpanded([matchedElement?.menuItem.rootName]));
    dispatch(actions.menuItemSetSelected([matchedElement?.menuItem.rootName, matchedElement?.menuItem.parentName]));

    // If matched element is found, navigate to the element
    if (matchedElement) {
      if (matchedElement.tilpasset === 1) {
        navigate(`${Utils.HOMEPAGE_CONTEXT}/${matchedElement.code}`);
      } else if (!isTransGetInProgress) {
        const { rootName, parentName, name, tilpasset, prodSynlig, kodeSynlig, labelSynlig, menuSynlig, description } = matchedElement.menuItem;
        const menuItemTarget = new MenuItemTarget(rootName, parentName, name, matchedElement.code, tilpasset, prodSynlig, kodeSynlig, labelSynlig, menuSynlig, false, description);
        //        
        if (Enums.lookupAndSwitchTransURL(matchedElement.code) !== matchedElement.code) {
          navigate(`${Utils.HOMEPAGE_CONTEXT}/${Enums.lookupAndSwitchTransURL(matchedElement.code)}`);
        }
        else {
          dispatch(actions.transGet(menuItemTarget));
        }
      }
      // Set the selected and expanded menu items
    }

    // Clean up
    setSearchVal('');
    dispatch(actions.setSearchInputFocused(false));
    dispatch(actions.setSearchInputValue(''));
  };

  //
  const menuOptions = () => {
    const options: SearchSel[] = [];
    if (menuItems && menuItems.length > 0) {
      menuItems.forEach((item: MenuEl) => {
        item.nodes.forEach((subItem: MenuEl) => {
          subItem.nodes.forEach((last: MenuEl) => {
            last.nodes.forEach((lst: MenuEl) => {
              if (+lst.menuSynlig !== 0) {
                options.push({ code: lst.code, tilpasset: +lst.tilpasset, name: lst.description, indexing: lst.code + ' ' + lst.description, menuItem: lst } as SearchSel);
              }
            });
          });
        });
      });
    }
    return options;
  }

  return (
    <Paper component="form" className={classes.searchBar} style={{ backgroundColor: '#d30535', width: '100%' }}>
      <IconButton className={classes.iconButton} aria-label="menu" onClick={handleHomeClick}>
        <NavLink to={U.HOMEPAGE_CONTEXT + '/menu'}>
          {' '}
          <LogoSym width="20px" />
        </NavLink>
      </IconButton>
      <Autocomplete
        open={isSearchFieldFocused}
        PopperComponent={PopperMy}
        value={selectedSearchOption}
        // closeIcon={false}
        id="code-select"
        ref={formRef}
        noOptionsText={'Ingen alternativer funnet.'}
        clearOnEscape={true}
        clearIcon={null}
        clearText={'clear'}
        forcePopupIcon={false}
        options={menuOptions().sort((a, b) => -b.indexing.localeCompare(a.indexing))}
        onChange={(event: any, val: any) => {
          if (val) {
            setSearchVal(val.code);
            handleSearchClick(event, val.code);
          }
        }}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            dispatch(actions.setSearchInputFocused(false));
            handleSearchClick(e);
          } else dispatch(actions.setSearchInputFocused(true));
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          dispatch(actions.setSearchInputValue(newInputValue));
          if (newInputValue.length > 0) dispatch(actions.setSearchInputPopulatedState(true));
        }}
        className={classes.searchCss}
        onFocus={() => { dispatch(actions.setSearchInputFocused(true)); }}
        onBlur={() => { dispatch(actions.setSearchInputFocused(false)); }}
        autoHighlight={true}
        getOptionLabel={(option: any) => option.indexing || ''}
        isOptionEqualToValue={(option: any, value: any) => { return value.fieldType === option.fieldType; }}
        onHighlightChange={(event, newInputValue) => { setSearchVal(newInputValue?.code || ''); }}
        filterOptions={(options, s) => {
          const _result: SearchSel[] = [];
          // first pass searches code
          options.forEach((element) => { if (element.code.startsWith(s.inputValue.toUpperCase())) { _result.push(element); } });
          // second pass searches and add indexing
          options.forEach((element: SearchSel) => {
            if (!_result.includes(element) && element.indexing.toLowerCase().includes(s.inputValue.toLowerCase())) {
              if (!s.inputValue.toLowerCase().startsWith(element.code.toLowerCase())) _result.push(element);
            }
          });
          return _result;
        }}
        renderOption={(props: any, s: SearchSel) => {
          return (
            <li {...props} key={props.id} className={classes.inputRoot}
              onContextMenu={(event) =>
                handleRightClick(
                  event,
                  new MenuItemTarget(
                    s.menuItem.rootName,
                    s.menuItem.parentName,
                    s.menuItem.name,
                    s.menuItem.code,
                    s.menuItem.tilpasset,
                    s.menuItem.prodSynlig,
                    s.menuItem.kodeSynlig,
                    s.menuItem.labelSynlig,
                    s.menuItem.menuSynlig,
                    s.menuItem.hasChildren,
                    s.menuItem.description
                  )
                )
              }
            >
              <span className={classes.buttonCode}>
                {s.code}
                <span className={classes.buttonDesc}>{s.name}</span>{' '}
              </span>
              <div className={classes.path}>
                {s.menuItem.rootName ? <span className={classes.rootName}>{s.menuItem.rootName}</span> : null}
                {s.menuItem.parentName ? (
                  <span className={classes.parentName}>
                    {' '}
                    {'>'} {s.menuItem.parentName}
                  </span>
                ) : null}
                {s.menuItem.heading ? (
                  <span className={classes.headName}>
                    {' '}
                    {'>'} {s.menuItem.heading}
                  </span>
                ) : null}
              </div>
            </li>
          );
        }}
        renderInput={(params) =>
          <Tooltip classes={{
            tooltip: classes.customTooltip,
            arrow: classes.customArrow

          }} title={'CTRL + S'} placement={"right"} arrow>
            <span>
              <TextField onKeyDown={(event) => { // prevent form submit on enter
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
                {...params} label="Søk i Online" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />
            </span>
          </Tooltip>
        }
      />
      <IconButton className={classes.iconButton} aria-label="search" onClick={handleSearchClick}>
        <SearchIcon style={{ color: 'white' }} />
      </IconButton>
    </Paper>
  );
}
